import {
  FormControlLabel, Grid, Link, Radio, RadioGroup, TextField,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { SaleType } from 'api/query/catalogue';
import { CataloguePayload } from 'api/query/catalogue/catalogue-payload';
import Datepicker from 'components/datepicker';
import Dropdown from 'components/dropdown';
import { DropdownItem } from 'components/dropdown/dropdown-item';
import FormErrorMessage from 'components/form-error-message';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CatalogueFormField, catalogueFormValidation } from '..';
import {
  getCatalogueInitialState, getCountries, getStates, getAnimalTemplateUrl,
} from '../create-catalogue-form-utils';
import { CreateSaleCatalogueProps } from './create-sale-catalogue-props';
import Button from '../../../../components/button';
import { ExportOutlinedIcon } from '../../../../components/icons/export-outlined';

/**
 * Create a sales catalogue.
 *
 * Step 1 of the create/edit sales catalogue.
 * @param props
 */
export const CreateSaleCatalogue: React.FC<CreateSaleCatalogueProps> = (props) => {
  const {
    catalogue,
    apiErrors,
  } = props;

  const {
    register, errors, setValue, watch, control,
  } = useFormContext();

  const [cata] = useState<CataloguePayload>(getCatalogueInitialState(catalogue));
  const [countries] = useState<DropdownItem[]>(getCountries());
  const [states, setStates] = useState<DropdownItem[]>(getStates(cata.country));

  // Watch sale end date so that the default end date can be set.
  const saleEndDate: Moment = watch(CatalogueFormField.SaleEndDate);

  /**
   * Fired when a State dropdown value changes.
   *
   * Manually set the state form value and triggers validation.
   * @param param0
   */
  const stateChanged = ([item]: DropdownItem[]) => {
    setValue(CatalogueFormField.State, item.value, true);
  };

  /**
   * Fired when the Country dropdown value changes.
   *
   * Manually sets the country form value and gets the new State dropdown items.
   * @param param0
   */
  const countryChanged = ([item]: DropdownItem[]) => {
    setValue(CatalogueFormField.Country, item.id);

    const newStates = getStates(item.value);

    // Update the validation rules for the State field
    register(CatalogueFormField.State, catalogueFormValidation[CatalogueFormField.State]);
    setValue(CatalogueFormField.State, '', true);

    setStates(newStates);
  };

  /**
   * Manually register the dropdowns as Material-UI does not correctly expose
   * the input through the inputRef prop.
   *
   * Refer to - https://github.com/react-hook-form/react-hook-form/issues/380
   */
  useEffect(() => {
    register(CatalogueFormField.State, catalogueFormValidation[CatalogueFormField.State]);
    register(CatalogueFormField.Country, catalogueFormValidation[CatalogueFormField.Country]);

    // Manually set the value from the catalogue after registration
    // to ensure the value is set on the form to avoid any
    // compatibility issues with Dropdown and Select.
    setValue(CatalogueFormField.State, cata.state);
    setValue(CatalogueFormField.Country, cata.country);
  }, [cata.country, cata.state, register, setValue]);

  /**
   * When a `saleEndDate` has been selected for the first time,
   * set the `listingEndDate` to 31 days from now.
   */
  useEffect(() => {
    if (saleEndDate) {
      const endDate = moment(saleEndDate).add(31, 'days');
      setValue(CatalogueFormField.ListingEndDate, endDate, true);
    }
  }, [saleEndDate, setValue]);

  return (
    <Grid
      className="form-container"
      item
      xs={12}
      sm={10}
      md={6}
    >
      <div className="intro">
        <Grid
          container
          justify="center"
        >
          <p>
            Before you start, download the Animal Listing template. This will allow you to upload multiple animals
            details quickly when creating you sale catalogue.<br />
            <i>Do not enter information or delete the cells that have yellow highlighting.</i>
          </p>
        </Grid>
        <Link
          className="intro__button"
          href="/Sales catalogue animal list template.xlsx"
          target="_blank"
          download
        >
          <Button
            variant="text"
            startIcon={<ExportOutlinedIcon />}
          >
            Animal listing template
          </Button>
        </Link>
        <hr className="MuiDivider-root" />
      </div>
      {apiErrors.map((e) => (
        <Alert
          key={e.errorType}
          severity="error"
        >
          {e.errorMessage}
        </Alert>
      ))}
      {/** Sales information */}
      <div className="form-section">
        <div className="form-section__header">Sales information</div>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
          >
            <RadioGroup defaultValue={cata.saleType?.toString()}>
              <Grid container>
                <Grid
                  item
                  xs={6}
                >
                  <FormControlLabel
                    value={SaleType.SingleVendorAuction.toString()}
                    label="Single-vendor auction"
                    control={(
                      <Radio
                        inputProps={{
                          name: CatalogueFormField.SaleType,
                        }}
                        inputRef={register}
                      />
                                      )}
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                >
                  <FormControlLabel
                    value={SaleType.PrivateTreatySale.toString()}
                    label="Private treaty sale"
                    control={(
                      <Radio
                        inputProps={{
                          name: CatalogueFormField.SaleType,
                        }}
                        inputRef={register}
                      />
                                      )}
                  />
                </Grid>
              </Grid>
            </RadioGroup>
          </Grid>
          <Grid
            item
            xs={12}
          >
            {/** Sale Title */}
            <TextField
              label="Sale Title"
              defaultValue={cata.title}
              name={CatalogueFormField.Title}
              error={!!errors[CatalogueFormField.Title]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.Title}
                />
                          )}
              inputRef={register(catalogueFormValidation[CatalogueFormField.Title])}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            {/** Flock Name */}
            <TextField
              label="Flock Name"
              defaultValue={cata.flock}
              name={CatalogueFormField.FlockName}
              error={!!errors[CatalogueFormField.FlockName]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.FlockName}
                />
                          )}
              inputRef={register(catalogueFormValidation[CatalogueFormField.FlockName])}
            />
          </Grid>
        </Grid>
      </div>
      {/** Sale & listing dates */}
      <div className="form-section">
        <div className="form-section__header">Sale & listing dates</div>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={6}
          >
            {/** Sale start date */}
            <Controller
              as={(
                <Datepicker
                  label="Sale start date"
                  error={!!errors[CatalogueFormField.SaleStartDate]}
                  helperText={(
                    <FormErrorMessage
                      errors={errors}
                      name={CatalogueFormField.SaleStartDate}
                    />
                                  )}
                />
                          )}
              name={CatalogueFormField.SaleStartDate}
              rules={catalogueFormValidation[CatalogueFormField.SaleStartDate]}
              defaultValue={cata.saleStartDate}
              control={control}
            />
          </Grid>
          <Grid
            item
            xs={6}
          >
            {/** Sale end date */}
            <Controller
              as={(
                <Datepicker
                  label="Sale end date"
                  error={!!errors[CatalogueFormField.SaleEndDate]}
                  helperText={(
                    <FormErrorMessage
                      errors={errors}
                      name={CatalogueFormField.SaleEndDate}
                    />
                                  )}
                  disablePast
                />
                          )}
              name={CatalogueFormField.SaleEndDate}
              rules={catalogueFormValidation[CatalogueFormField.SaleEndDate]}
              defaultValue={cata.saleEndDate}
              control={control}
            />
          </Grid>
          <Grid
            item
            xs={6}
          >
            {/** Show listing from */}
            <Controller
              as={(
                <Datepicker
                  label="Show listing from"
                  error={!!errors[CatalogueFormField.ListingStartDate]}
                  helperText={(
                    <FormErrorMessage
                      errors={errors}
                      name={CatalogueFormField.ListingStartDate}
                    />
                                  )}
                />
                          )}
              name={CatalogueFormField.ListingStartDate}
              rules={catalogueFormValidation[CatalogueFormField.ListingStartDate]}
              defaultValue={cata.listingStartDate}
              control={control}
            />
          </Grid>
          <Grid
            item
            xs={6}
          >
            {/** Remove listing on */}
            <Controller
              as={(
                <Datepicker
                  readOnly
                  label="Remove listing on"
                  disabled={!saleEndDate}
                  error={!!errors[CatalogueFormField.ListingEndDate]}
                  helperText={(
                    <FormErrorMessage
                      errors={errors}
                      name={CatalogueFormField.ListingEndDate}
                    />
                                  )}
                />
                          )}
              name={CatalogueFormField.ListingEndDate}
              defaultValue={cata.listingEndDate}
              control={control}
            />
          </Grid>
        </Grid>
      </div>
      {/** Location */}
      <div className="form-section">
        <div className="form-section__header">Location</div>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
          >
            {/** Property name (optional) */}
            <TextField
              label="Property Name (optional)"
              defaultValue={cata.propertyName}
              name={CatalogueFormField.PropertyName}
              error={!!errors[CatalogueFormField.PropertyName]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.PropertyName}
                />
                          )}
              inputRef={register(catalogueFormValidation[CatalogueFormField.PropertyName])}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            {/** Address (optional) */}
            <TextField
              label="Address (optional)"
              defaultValue={cata.address}
              name={CatalogueFormField.Address}
              error={!!errors[CatalogueFormField.Address]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.Address}
                />
                          )}
              inputRef={register(catalogueFormValidation[CatalogueFormField.Address])}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            {/** Postcode */}
            <TextField
              label="Town/city"
              defaultValue={cata.city}
              name={CatalogueFormField.Town}
              error={!!errors[CatalogueFormField.Town]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.Town}
                />
                          )}
              inputRef={register(catalogueFormValidation[CatalogueFormField.Town])}
            />
          </Grid>
          <Grid
            item
            xs={4}
          >
            {/** Postcode */}
            <TextField
              label="Postcode"
              type="number"
              defaultValue={cata.postcode}
              name={CatalogueFormField.Postcode}
              error={!!errors[CatalogueFormField.Postcode]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.Postcode}
                />
                          )}
              inputRef={register(catalogueFormValidation[CatalogueFormField.Postcode])}
            />
          </Grid>
          <Grid
            item
            xs={4}
          >
            {/** State */}
            <Dropdown
              label="State"
              error={!!errors[CatalogueFormField.State]}
              disabled={!states.length}
              placeholder="Select State"
              item={states.find((s) => s.id === cata.state)}
              items={states}
              onSelectionChange={stateChanged}
            />
            <FormErrorMessage
              className="form__error-message"
              errors={errors}
              name={CatalogueFormField.State}
            />
          </Grid>
          <Grid
            item
            xs={4}
          >
            {/** Country */}
            <Dropdown
              label="Country"
              item={countries.find((c) => c.id === cata.country)}
              items={countries}
              onSelectionChange={countryChanged}
            />
          </Grid>
        </Grid>
      </div>
      {/** Contact details */}
      <div className="form-section">
        <div className="form-section__header">Contact details</div>
        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
          >
            {/** Contact name */}
            <TextField
              label="Contact Name"
              defaultValue={cata.contactName}
              name={CatalogueFormField.ContactName}
              error={!!errors[CatalogueFormField.ContactName]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.ContactName}
                />
                          )}
              inputRef={register(catalogueFormValidation[CatalogueFormField.ContactName])}
            />
          </Grid>
          <Grid
            item
            xs={12}
          >
            {/** Email */}
            <TextField
              label="Email Address (optional)"
              defaultValue={cata.contactEmail}
              name={CatalogueFormField.Email}
              error={!!errors[CatalogueFormField.Email]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.Email}
                />
                          )}
              inputRef={register}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
          >
            {/** Phone */}
            <TextField
              label="Phone Number (optional)"
              type="number"
              defaultValue={cata.contactPhoneNumber}
              name={CatalogueFormField.Phone}
              error={!!errors[CatalogueFormField.Phone]}
              helperText={(
                <FormErrorMessage
                  errors={errors}
                  name={CatalogueFormField.Phone}
                />
                          )}
              inputRef={register}
            />
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};

// (CreateSaleCatalogue as any).whyDidYouRender = {
//   customName: 'CreateSaleCatalogue',
// };
